/**
 * Bulk Import Parser for Emissions Activities
 *
 * Parses CSV/XLSX files and validates activity data using the xlsx library.
 */

import * as XLSX from 'xlsx';
import { XlsxFile } from '@utils/file/XlsxFile';
import {
  EmissionScope,
  Scope1ActivityType,
  ActivityUnit,
  FuelType,
  VehicleType,
  FugitiveGasType,
  RefrigerantGasType,
  ProcessType,
  CreateActivityRequest,
  ActivityType,
  getStationaryFuels,
  getMobileFuels,
  getValidUnits,
  type ActivityTypeWithUnits,
} from '../types/emissions';
import {
  isFugitiveGas,
  isValidActivityType,
  isValidUnit,
  isUnitValidForActivity,
  isValidFuelType,
  isValidVehicleType,
  isValidFugitiveGasType,
  isValidProcessType,
  type ValidationError,
} from './activityValidation';

export type { ValidationError };

export interface ParsedRow {
  rowNumber: number;
  data: Partial<CreateActivityRequest>;
  rawData: Record<string, string>;
  errors: ValidationError[];
  isValid: boolean;
}

export interface ParseResult {
  rows: ParsedRow[];
  validCount: number;
  errorCount: number;
  headers: string[];
}

// Header name mapping (handles variations in column names)
const HEADER_MAPPINGS: Record<string, string> = {
  // Activity Type variations
  'activity type': 'activityType',
  'activity type*': 'activityType',
  'activitytype': 'activityType',
  'type': 'activityType',

  // Period Start variations
  'period start': 'periodStart',
  'period start*': 'periodStart',
  'periodstart': 'periodStart',
  'start date': 'periodStart',
  'start': 'periodStart',

  // Period End variations
  'period end': 'periodEnd',
  'period end*': 'periodEnd',
  'periodend': 'periodEnd',
  'end date': 'periodEnd',
  'end': 'periodEnd',

  // Quantity variations
  'quantity': 'quantity',
  'quantity*': 'quantity',
  'amount': 'quantity',

  // Unit variations
  'unit': 'unit',
  'unit*': 'unit',
  'units': 'unit',

  // Fuel Type variations
  'fuel type': 'fuelType',
  'fuel type*': 'fuelType',
  'fueltype': 'fuelType',
  'fuel': 'fuelType',

  // Vehicle Type variations
  'vehicle type': 'vehicleType',
  'vehicle type*': 'vehicleType',
  'vehicletype': 'vehicleType',
  'vehicle': 'vehicleType',

  // Gas / Refrigerant Type variations (both map to refrigerantType)
  'gas / refrigerant type': 'refrigerantType',
  'gas / refrigerant type*': 'refrigerantType',
  'refrigerant type': 'refrigerantType',
  'refrigerant type*': 'refrigerantType',
  'refrigeranttype': 'refrigerantType',
  'refrigerant': 'refrigerantType',
  'gas type': 'refrigerantType',
  'gastype': 'refrigerantType',

  // Process Type variations
  'process type': 'processType',
  'process type*': 'processType',
  'processtype': 'processType',
  'process': 'processType',

  // Region variations
  'region': 'region',
  'region*': 'region',
  'country': 'region',
  'location': 'region',

  // Region Fallback variations
  'region fallback': 'regionFallback',
  'regionfallback': 'regionFallback',

  // Year Fallback variations
  'year fallback': 'yearFallback',
  'yearfallback': 'yearFallback',

  // Notes variations
  'notes': 'notes',
  'note': 'notes',
  'comments': 'notes',
  'description': 'notes',

  // Name / Label variations
  'name / label': 'name',
  'name/label': 'name',
  'name': 'name',
  'label': 'name',
};

/**
 * Normalize header name to field key
 */
const normalizeHeader = (header: string): string => {
  const normalized = header.toLowerCase().trim();
  return HEADER_MAPPINGS[normalized] || normalized;
};

/**
 * Get value from row using flexible header matching
 */
const getRowValue = (row: Record<string, string>, targetField: string): string => {
  // First try direct match
  if (row[targetField] !== undefined) {
    return String(row[targetField]).trim();
  }

  // Try all keys and find match via header mapping
  for (const key of Object.keys(row)) {
    const normalizedKey = normalizeHeader(key);
    if (normalizedKey === targetField) {
      return String(row[key]).trim();
    }
  }

  return '';
};


/**
 * Convert Excel serial number to YYYY-MM-DD date string
 * xlsx library converts date strings to serial numbers during parsing.
 * Excel dates are days since 1899-12-30 (with a leap year bug for dates before March 1900)
 */
const excelSerialToDate = (serial: number): string => {
  const excelEpoch = new Date(1899, 11, 30); // Dec 30, 1899
  const date = new Date(excelEpoch.getTime() + serial * 24 * 60 * 60 * 1000);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Parse date value - handles both YYYY-MM-DD strings and Excel serial numbers
 * xlsx library converts dates to serial numbers during read, so we convert them back
 */
const parseDateValue = (value: string): string => {
  if (!value) {
    return '';
  }

  // Check if it's already in YYYY-MM-DD format
  const isoDateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (isoDateRegex.test(value)) {
    return value;
  }

  // Check if it's an Excel serial number (numeric value typically between 1 and 100000)
  const serial = parseFloat(value);
  if (!isNaN(serial) && serial > 0 && serial < 100000) {
    return excelSerialToDate(serial);
  }

  // Return as-is if we can't parse it
  return value;
};

/**
 * Validate date format (YYYY-MM-DD)
 */
const isValidDate = (dateStr: string): boolean => {
  if (!dateStr) {
    return false;
  }
  const regex = /^\d{4}-\d{2}-\d{2}$/;
  if (!regex.test(dateStr)) {
    return false;
  }
  const date = new Date(dateStr);
  return !isNaN(date.getTime());
};

/**
 * Validate and transform a single row
 */
const validateAndTransformRow = (
  row: Record<string, string>,
  rowNumber: number,
  scope: EmissionScope
): ParsedRow => {
  const errors: ValidationError[] = [];

  // Extract values with flexible header matching
  const activityType = getRowValue(row, 'activityType');
  // Parse dates - xlsx converts date strings to serial numbers, so we convert them back
  const periodStart = parseDateValue(getRowValue(row, 'periodStart'));
  const periodEnd = parseDateValue(getRowValue(row, 'periodEnd'));
  const quantityStr = getRowValue(row, 'quantity');
  const unit = getRowValue(row, 'unit');
  const fuelType = getRowValue(row, 'fuelType');
  const vehicleType = getRowValue(row, 'vehicleType');
  const refrigerantType = getRowValue(row, 'refrigerantType');
  const processType = getRowValue(row, 'processType');
  // Template dropdowns show "SG – Singapore" format — extract just the code
  const rawRegion = getRowValue(row, 'region');
  const region = rawRegion.includes('–') ? rawRegion.split('–')[0].trim() : rawRegion;
  const regionFallbackRaw = getRowValue(row, 'regionFallback');
  const yearFallbackRaw = getRowValue(row, 'yearFallback');
  const notes = getRowValue(row, 'notes');
  const name = getRowValue(row, 'name');

  // Required field checks
  if (!activityType) {
    errors.push({ field: 'activityType', message: 'Activity Type is required' });
  } else if (!isValidActivityType(activityType, scope)) {
    errors.push({
      field: 'activityType',
      message: `Invalid activity type for ${scope}`,
    });
  }

  if (!periodStart) {
    errors.push({ field: 'periodStart', message: 'Period Start is required' });
  } else if (!isValidDate(periodStart)) {
    errors.push({ field: 'periodStart', message: 'Invalid date format (use YYYY-MM-DD)' });
  }

  if (!periodEnd) {
    errors.push({ field: 'periodEnd', message: 'Period End is required' });
  } else if (!isValidDate(periodEnd)) {
    errors.push({ field: 'periodEnd', message: 'Invalid date format (use YYYY-MM-DD)' });
  }

  // Date range validation
  if (periodStart && periodEnd && isValidDate(periodStart) && isValidDate(periodEnd)) {
    const start = new Date(periodStart);
    const end = new Date(periodEnd);
    if (start > end) {
      errors.push({ field: 'periodEnd', message: 'End date must be after start date' });
    }
  }

  if (!quantityStr) {
    errors.push({ field: 'quantity', message: 'Quantity is required' });
  } else {
    const quantity = parseFloat(quantityStr);
    if (isNaN(quantity) || quantity <= 0) {
      errors.push({ field: 'quantity', message: 'Quantity must be a positive number' });
    }
  }

  if (!unit) {
    errors.push({ field: 'unit', message: 'Unit is required' });
  } else if (!isValidUnit(unit)) {
    errors.push({ field: 'unit', message: `Invalid unit "${unit}". Valid values: ${Object.values(ActivityUnit).join(', ')}` });
  } else if (activityType && !isUnitValidForActivity(unit, activityType, fuelType)) {
    const validUnits = getValidUnits(activityType as ActivityTypeWithUnits, fuelType as FuelType || undefined);
    errors.push({ field: 'unit', message: `Unit "${unit}" is not valid for ${activityType}. Valid units: ${validUnits.join(', ')}` });
  }

  if (!region) {
    errors.push({ field: 'region', message: 'Region is required' });
  } else if (region.length < 2 || region.length > 10) {
    errors.push({ field: 'region', message: 'Region must be 2-10 characters' });
  } else if (/^[A-Z]{3}$/.test(region)) {
    errors.push({
      field: 'region',
      message: `"${region}" looks like a 3-letter ISO alpha-3 code. Emission factors use ISO alpha-2 (2-letter) codes — e.g. SG for Singapore, AE for UAE, SA for Saudi Arabia, US for United States.`,
    });
  }

  // Activity-type-specific required field validation
  if (activityType === Scope1ActivityType.StationaryCombustion && !fuelType) {
    errors.push({ field: 'fuelType', message: 'Fuel Type is required for stationary combustion' });
  }
  if (activityType === Scope1ActivityType.MobileCombustion && !vehicleType) {
    errors.push({ field: 'vehicleType', message: 'Vehicle Type is required for mobile combustion' });
  }
  if (activityType === Scope1ActivityType.FugitiveEmissions && !refrigerantType) {
    errors.push({ field: 'refrigerantType', message: 'Gas / Refrigerant Type is required for fugitive emissions' });
  }
  if (activityType === Scope1ActivityType.ProcessEmissions && !processType) {
    errors.push({ field: 'processType', message: 'Process Type is required for process emissions' });
  }

  // Optional field validation
  if (fuelType && !isValidFuelType(fuelType, activityType)) {
    const validFuels = activityType === Scope1ActivityType.StationaryCombustion
      ? getStationaryFuels()
      : activityType === Scope1ActivityType.MobileCombustion
        ? getMobileFuels()
        : Object.values(FuelType);
    errors.push({ field: 'fuelType', message: `Invalid fuel type "${fuelType}". Valid values: ${validFuels.join(', ')}` });
  }
  if (vehicleType && !isValidVehicleType(vehicleType)) {
    errors.push({ field: 'vehicleType', message: `Invalid vehicle type "${vehicleType}". Valid values: ${Object.values(VehicleType).join(', ')}` });
  }
  if (refrigerantType && !isValidFugitiveGasType(refrigerantType)) {
    errors.push({ field: 'refrigerantType', message: `Invalid gas/refrigerant type "${refrigerantType}". Valid values: ${Object.values(FugitiveGasType).join(', ')}` });
  }
  if (processType && !isValidProcessType(processType)) {
    errors.push({ field: 'processType', message: `Invalid process type "${processType}". Valid values: ${Object.values(ProcessType).join(', ')}` });
  }
  // Build activity data
  const data: Partial<CreateActivityRequest> = {
    scope,
    activityType: activityType as ActivityType,
    periodStart,
    periodEnd,
    quantity: parseFloat(quantityStr) || 0,
    unit: unit as ActivityUnit,
    region,
    fuelType: fuelType ? (fuelType as FuelType) : undefined,
    vehicleType: vehicleType ? (vehicleType as VehicleType) : undefined,
    refrigerantType: refrigerantType && !isFugitiveGas(refrigerantType)
      ? (refrigerantType as RefrigerantGasType) : undefined,
    gasType: refrigerantType && isFugitiveGas(refrigerantType)
      ? (refrigerantType as FugitiveGasType) : undefined,
    processType: processType ? (processType as ProcessType) : undefined,
    regionFallback: regionFallbackRaw ? regionFallbackRaw.toUpperCase() !== 'FALSE' : true,
    yearFallback: yearFallbackRaw ? yearFallbackRaw.toUpperCase() !== 'FALSE' : true,
    notes: notes || undefined,
    name: name || undefined,
  };

  return {
    rowNumber,
    data,
    rawData: row,
    errors,
    isValid: errors.length === 0,
  };
};

/**
 * Parse import file (CSV or XLSX) and validate data
 */
export const parseImportFile = (file: File, scope: EmissionScope): Promise<ParseResult> => {
  return new Promise((resolve, reject) => {
    XlsxFile(file, (workbook) => {
      try {
        const sheetName = workbook.SheetNames[0];
        if (!sheetName) {
          reject(new Error('No sheets found in file'));
          return;
        }

        const worksheet = workbook.Sheets[sheetName];
        // Use raw: true to preserve original string values (prevents date conversion to serial numbers)
        const rawData = XLSX.utils.sheet_to_json(worksheet, { raw: true }) as Record<string, unknown>[];

        if (rawData.length === 0) {
          resolve({
            rows: [],
            validCount: 0,
            errorCount: 0,
            headers: [],
          });
          return;
        }

        // Get headers from first row
        const headers = Object.keys(rawData[0] || {});

        // Skip the descriptions row if present (backend templates include it as row 2).
        // Detected by YYYY in the period start field (description text, not a real date).
        const dataRowsWithIndex = rawData
          .map((row, index) => ({ row: row as Record<string, string>, excelRow: index + 2 }))
          .filter(({ row }) => {
            const periodStart = getRowValue(row, 'periodStart');
            return !periodStart.toUpperCase().includes('YYYY');
          });

        const rows = dataRowsWithIndex.map(({ row, excelRow }) =>
          validateAndTransformRow(row, excelRow, scope)
        );

        resolve({
          rows,
          validCount: rows.filter((r) => r.isValid).length,
          errorCount: rows.filter((r) => !r.isValid).length,
          headers,
        });
      } catch (error) {
        reject(error instanceof Error ? error : new Error(String(error)));
      }
    });
  });
};

/**
 * Convert parsed rows to CreateActivityRequest array (valid rows only)
 */
export const toActivityRequests = (parseResult: ParseResult): CreateActivityRequest[] => {
  return parseResult.rows
    .filter((row) => row.isValid)
    .map((row) => row.data as CreateActivityRequest);
};
